import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, put, del, postMultipart } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";

function clientsListQuery(params) {
  const q = { page: params.page ?? 1, limit: params.limit ?? DEFAULT_LIST_PAGE_SIZE };
  for (const key of ["search", "name", "status", "contactPerson"]) {
    const v = params[key];
    if (v != null && String(v).trim() !== "") q[key] = String(v).trim();
  }
  return q;
}

export const fetchClientsData = createAsyncThunk(
  "clients/fetchData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const getAll = params.getAll === true;
      const endpoint = getAll ? API_ENDPOINTS.CLIENTS.GET_ALL_LIST : API_ENDPOINTS.CLIENTS.GET_ALL;
      const query = getAll ? {} : clientsListQuery(params);
      const response = await get(endpoint, query);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch clients data"));
    }
  }
);

export const fetchClientById = createAsyncThunk(
  "clients/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.CLIENTS.GET_BY_ID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch client data"));
    }
  }
);

export const fetchClientDashboard = createAsyncThunk(
  "clients/fetchDashboard",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.CLIENTS.GET_DASHBOARD(clientId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch client dashboard"));
    }
  }
);

export const createClient = createAsyncThunk(
  "clients/create",
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.CLIENTS.CREATE, clientData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create client"));
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/update",
  async ({ id, clientData }, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.CLIENTS.UPDATE(id), clientData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update client"));
    }
  }
);

export const deleteClient = createAsyncThunk("clients/delete", async (id, { rejectWithValue }) => {
  try {
    await del(API_ENDPOINTS.CLIENTS.DELETE(id));
    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error, "Failed to delete client"));
  }
});

export const fetchSaasModules = createAsyncThunk(
  "clients/fetchSaasModules",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.CLIENTS.GET_SAAS_MODULES);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch SaaS modules"));
    }
  }
);

export const fetchClientSaasModules = createAsyncThunk(
  "clients/fetchClientSaasModules",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.CLIENTS.GET_CLIENT_SAAS_MODULES(clientId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch client SaaS modules"));
    }
  }
);

export const setClientSaasModules = createAsyncThunk(
  "clients/setSaasModules",
  async ({ clientId, modules }, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.CLIENTS.SET_SAAS_MODULES(clientId), { modules });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to set client SaaS modules"));
    }
  }
);

export const fetchClientAttachments = createAsyncThunk(
  "clients/fetchAttachments",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.CLIENTS.GET_ATTACHMENTS(clientId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch attachments"));
    }
  }
);

export const uploadClientAttachments = createAsyncThunk(
  "clients/uploadAttachments",
  async ({ clientId, files }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      (files || []).forEach((item, index) => {
        const file = item?.file ?? item;
        if (file) formData.append("files", file, file.name || `file-${index}`);
      });
      const response = await postMultipart(
        API_ENDPOINTS.CLIENTS.UPLOAD_ATTACHMENTS(clientId),
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to upload attachments"));
    }
  }
);

export const getClientAttachmentDownloadUrl = createAsyncThunk(
  "clients/getAttachmentUrl",
  async ({ clientId, attachmentId, expiresIn }, { rejectWithValue }) => {
    try {
      const params = expiresIn != null ? { expiresIn } : {};
      const response = await get(
        API_ENDPOINTS.CLIENTS.GET_ATTACHMENT_URL(clientId, attachmentId),
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to get download URL"));
    }
  }
);

export const deleteClientAttachment = createAsyncThunk(
  "clients/deleteAttachment",
  async ({ clientId, attachmentId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.CLIENTS.DELETE_ATTACHMENT(clientId, attachmentId),
        {},
        {
          skipToast: true,
        }
      );
      return attachmentId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete attachment"));
    }
  }
);
